(function () {
  'use strict';

  /* =========================================================
     CONFIG
  ========================================================= */

  const STORAGE_KEY = 'bpg_bulk_local_delivery';

  const BULK_THRESHOLD = 25000; // 25kg in grams
  const MIN_LOCAL_DELIVERY_AMOUNT = 5000; // $150 in cents

  const BLUE_STAR_VENDORS = [
    'blue star'
  ];

  const THIRD_PARTY_VENDORS = [
    'wff',
    'wff aussie fulfilment center',
    'cj',
    'cj dropshipping',
    'cjdropshipping'
  ];

  let cartRequest = null;
  let refreshTimer = null;
  let storeLocationCache = null;


  /* =========================================================
     STORAGE
  ========================================================= */

  function getDeliveryData() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);

      if (!saved) {
        return null;
      }

      const data = JSON.parse(saved);

      if (
        !data ||
        typeof data.eligible !== 'boolean'
      ) {
        return null;
      }

      return data;

    } catch (error) {
      return null;
    }
  }


  function saveDeliveryData(data) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(data)
      );
    } catch (error) {
      console.warn(
        'BPG delivery storage error:',
        error
      );
    }
  }


  function clearDeliveryData() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn(
        'BPG delivery clear error:',
        error
      );
    }
  }


  /* =========================================================
     CART API
  ========================================================= */

  async function getCart() {
    if (cartRequest) {
      return cartRequest;
    }

    cartRequest = fetch(
      '/cart.js',
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error(
            'Unable to read cart.'
          );
        }

        return response.json();
      })
      .finally(function () {
        cartRequest = null;
      });

    return cartRequest;
  }


  /* =========================================================
     VENDOR HELPERS
  ========================================================= */

  function normalizeVendor(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }


  function isBlueStarItem(item) {
    return BLUE_STAR_VENDORS.includes(
      normalizeVendor(item && item.vendor)
    );
  }


  function isThirdPartyItem(item) {
    return THIRD_PARTY_VENDORS.includes(
      normalizeVendor(item && item.vendor)
    );
  }


  /* =========================================================
     CART CLASSIFICATION
  ========================================================= */

  function getCartType(cart) {
    const items =
      cart &&
      Array.isArray(cart.items)
        ? cart.items
        : [];

    if (!items.length) {
      return {
        type: 'empty',
        isEmpty: true,
        isRetailOnly: false,
        hasBlueStar: false,
        hasThirdParty: false,
        isMixed: false,
        isBulk: false
      };
    }

    let hasRetail = false;
    let hasBlueStar = false;
    let hasThirdParty = false;

    items.forEach(function (item) {
      if (isBlueStarItem(item)) {
        hasBlueStar = true;
        return;
      }

      if (isThirdPartyItem(item)) {
        hasThirdParty = true;
        return;
      }

      hasRetail = true;
    });

    const categoryCount = [
      hasRetail,
      hasBlueStar,
      hasThirdParty
    ].filter(Boolean).length;

    const isMixed =
      categoryCount > 1;

    let type = 'retail_only';

    if (isMixed) {
      type = 'mixed';
    } else if (hasBlueStar) {
      type = 'blue_star_only';
    } else if (hasThirdParty) {
      type = 'third_party_only';
    }

    return {
      type: type,

      isEmpty: false,

      isRetailOnly:
        type === 'retail_only',

      hasBlueStar:
        hasBlueStar,

      hasThirdParty:
        hasThirdParty,

      isMixed:
        isMixed,

      isBulk:
        Number(cart.total_weight || 0) >=
        BULK_THRESHOLD
    };
  }


/* =========================================================
   LOCAL PICKUP
========================================================= */

function canShowLocalPickup(cart) {
  const cartType = getCartType(cart);

  /*
   * Pickup is NOT allowed when the cart contains
   * WFF / CJ / third-party products.
   *
   * Blue Star and Retail Shop / All locations
   * products remain eligible for pickup.
   */

  return (
    !cartType.isEmpty &&
    !cartType.hasThirdParty
  );
}


function updateLocalPickupVisibility(cart) {
  const pickupButtons =
    document.querySelectorAll(
      '[data-bpg-local-pickup]'
    );

  if (!pickupButtons.length) {
    return;
  }

  const showPickup =
    canShowLocalPickup(cart);

  pickupButtons.forEach(
    function (button) {
      button.hidden = !showPickup;
    }
  );
}
  /* =========================================================
     DELIVERY ELIGIBILITY
  ========================================================= */

  function getEligibilityReason(
    distance,
    radius,
    cartTotal
  ) {
    if (
      distance > radius
    ) {
      return 'outside_radius';
    }

    if (
      cartTotal <
      MIN_LOCAL_DELIVERY_AMOUNT
    ) {
      return 'minimum_order';
    }

    return 'eligible';
  }


  function getEligibilityMessage(reason) {
    switch (reason) {

      case 'minimum_order':
        return (
          'A minimum order of $150 is required for free local delivery.'
        );

      case 'outside_radius':
        return (
          'Unfortunately, this address is outside our 10 km local delivery area.'
        );

      case 'blue_star':
        return (
          'Local delivery is not available for Blue Star products.'
        );

      case 'third_party':
        return (
          'Local delivery is not available for third-party products.'
        );

      case 'mixed':
        return (
          'Local delivery is not available for mixed carts containing products from different fulfilment sources.'
        );

      default:
        return '';
    }
  }


  /* =========================================================
     CART ATTRIBUTES
  ========================================================= */

  async function updateCartAttributes(attributes) {
    const response = await fetch(
      '/cart/update.js',
      {
        method: 'POST',

        credentials:
          'same-origin',

        headers: {
          'Content-Type':
            'application/json',

          Accept:
            'application/json'
        },

        body: JSON.stringify({
          attributes: attributes
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to update cart.'
      );
    }

    return response.json();
  }


  async function saveEligibilityToCart(data) {
    const attributes = {
      'Delivery Type':
        data &&
        data.eligible === true
          ? 'Local Delivery'
          : '',

      'Delivery Address':
        data &&
        data.fullAddress
          ? data.fullAddress
          : '',

      'Delivery Suburb':
        data &&
        data.suburb
          ? data.suburb
          : '',

      'Delivery Postcode':
        data &&
        data.postcode
          ? data.postcode
          : '',

      'Local Delivery Eligibility':
        data &&
        data.eligible === true
          ? 'Eligible'
          : '',

      'Local Delivery Reason':
        data &&
        data.reason
          ? data.reason
          : '',

      'Distance from Store':
        data &&
        typeof data.distance !==
          'undefined'
          ? data.distance + ' km'
          : ''
    };

    await updateCartAttributes(
      attributes
    );
  }


  /* =========================================================
     DISTANCE
  ========================================================= */

  function getDistanceKm(
    lat1,
    lng1,
    lat2,
    lng2
  ) {
    const earthRadius = 6371;

    const toRadians =
      function (value) {
        return (
          value *
          Math.PI /
          180
        );
      };

    const dLat =
      toRadians(lat2 - lat1);

    const dLng =
      toRadians(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) *
      Math.sin(dLat / 2) +

      Math.cos(
        toRadians(lat1)
      ) *

      Math.cos(
        toRadians(lat2)
      ) *

      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

    return (
      2 *
      earthRadius *
      Math.atan2(
        Math.sqrt(a),
        Math.sqrt(1 - a)
      )
    );
  }


  /* =========================================================
     GEOCODING
  ========================================================= */

  async function searchAddress(query) {
    const endpoint =
      'https://nominatim.openstreetmap.org/search?' +

      new URLSearchParams({
        q: query,
        format: 'json',
        limit: '1',
        countrycodes: 'au',
        addressdetails: '1'
      }).toString();

    const response =
      await fetch(
        endpoint,
        {
          headers: {
            Accept:
              'application/json'
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        'Unable to check this address.'
      );
    }

    const results =
      await response.json();

    return (
      Array.isArray(results) &&
      results.length
    )
      ? results[0]
      : null;
  }


  async function geocodeCustomerAddress(
    address,
    suburb,
    postcode
  ) {
    const queries = [];

    const fullQuery =
      [
        address,
        suburb,
        postcode,
        'Australia'
      ]
        .filter(Boolean)
        .join(', ');

    if (fullQuery) {
      queries.push(fullQuery);
    }

    if (
      suburb &&
      postcode
    ) {
      queries.push(
        suburb +
        ', NSW ' +
        postcode +
        ', Australia'
      );
    }

    for (
      let i = 0;
      i < queries.length;
      i += 1
    ) {
      const result =
        await searchAddress(
          queries[i]
        );

      if (result) {
        return result;
      }
    }

    return null;
  }


  async function geocodeStore(modal) {
    if (storeLocationCache) {
      return storeLocationCache;
    }

    const storeAddress =
      modal.dataset.storeAddress;

    if (!storeAddress) {
      throw new Error(
        'Store location is not configured correctly.'
      );
    }

    storeLocationCache =
      await searchAddress(
        storeAddress
      );

    return storeLocationCache;
  }


  /* =========================================================
     MODAL HELPERS
  ========================================================= */

  function getModal() {
    return document.querySelector(
      '[data-bpg-local-delivery-modal]'
    );
  }


  function openModal() {
    const modal =
      getModal();

    if (!modal) {
      return;
    }

    hideModalMessage(modal);
    hideModalResult(modal);

    const saved =
      getDeliveryData();

    if (saved) {
      const addressInput =
        modal.querySelector(
          '[name="address"]'
        );

      const suburbInput =
        modal.querySelector(
          '[name="suburb"]'
        );

      const postcodeInput =
        modal.querySelector(
          '[name="postcode"]'
        );

      if (addressInput) {
        addressInput.value =
          saved.address || '';
      }

      if (suburbInput) {
        suburbInput.value =
          saved.suburb || '';
      }

      if (postcodeInput) {
        postcodeInput.value =
          saved.postcode || '';
      }
    }

    modal.hidden = false;

    modal.setAttribute(
      'aria-hidden',
      'false'
    );

    document.documentElement.classList.add(
      'bpg-local-delivery-modal-open'
    );
  }


  function closeModal() {
    const modal =
      getModal();

    if (!modal) {
      return;
    }

    modal.hidden = true;

    modal.setAttribute(
      'aria-hidden',
      'true'
    );

    document.documentElement.classList.remove(
      'bpg-local-delivery-modal-open'
    );
  }


  function showModalMessage(
    modal,
    message
  ) {
    const messageBox =
      modal.querySelector(
        '[data-bpg-local-delivery-message]'
      );

    if (!messageBox) {
      return;
    }

    messageBox.hidden = false;

    messageBox.textContent =
      message;
  }


  function hideModalMessage(
    modal
  ) {
    const messageBox =
      modal.querySelector(
        '[data-bpg-local-delivery-message]'
      );

    if (!messageBox) {
      return;
    }

    messageBox.hidden = true;

    messageBox.textContent =
      '';
  }


  function showModalResult(
    modal,
    html,
    isEligible
  ) {
    const result =
      modal.querySelector(
        '[data-bpg-local-delivery-result]'
      );

    if (!result) {
      return;
    }

    result.hidden = false;

    result.className =
      'bpg-local-delivery-result ' +
      (
        isEligible
          ? 'is-eligible'
          : 'is-not-eligible'
      );

    result.innerHTML = html;
  }


  function hideModalResult(
    modal
  ) {
    const result =
      modal.querySelector(
        '[data-bpg-local-delivery-result]'
      );

    if (!result) {
      return;
    }

    result.hidden = true;

    result.innerHTML = '';

    result.className =
      'bpg-local-delivery-result';
  }


  /* =========================================================
     UI STATE
  ========================================================= */

  function hideAllStates(
    container
  ) {
    container
      .querySelectorAll(
        '[data-bpg-delivery-state]'
      )
      .forEach(function (state) {
        state.hidden = true;
      });
  }


  function showState(
    container,
    selector
  ) {
    const state =
      container.querySelector(
        selector
      );

    if (state) {
      state.hidden = false;
    }
  }


  function updateAddress(
    container,
    address
  ) {
    container
      .querySelectorAll(
        '[data-bpg-selected-delivery-address]'
      )
      .forEach(function (element) {
        element.textContent =
          address || '';
      });
  }


  function updateCartDeliveryUI(
    cart
  ) {
    const cartType =
      getCartType(cart);

        updateLocalPickupVisibility(
            cart
          );


    const deliveryData =
      getDeliveryData();

    const containers =
      document.querySelectorAll(
        '[data-bpg-delivery-module]'
      );

    containers.forEach(
      function (container) {

        hideAllStates(
          container
        );

        /*
         * Empty cart.
         */

        if (cartType.isEmpty) {
          return;
        }


        /*
         * ONLY RETAIL SHOP PRODUCTS
         */

        if (
          cartType.isRetailOnly
        ) {

          /*
           * 25kg+ cart.
           */

          if (
            cartType.isBulk
          ) {

            if (
              deliveryData &&
              deliveryData.eligible
            ) {
              updateAddress(
                container,
                deliveryData.fullAddress
              );

              showState(
                container,
                '[data-bpg-state-local-eligible]'
              );

            } else if (
              deliveryData &&
              deliveryData.reason
            ) {
              showState(
                container,
                '[data-bpg-state-bulk-freight]'
              );

            } else {
              showState(
                container,
                '[data-bpg-state-bulk-retail]'
              );
            }

            return;
          }


          /*
           * Below 25kg.
           */

          if (
            deliveryData &&
            deliveryData.eligible
          ) {
            updateAddress(
              container,
              deliveryData.fullAddress
            );

            showState(
              container,
              '[data-bpg-state-local-eligible]'
            );

          } else {
            showState(
              container,
              '[data-bpg-state-retail-options]'
            );
          }

          return;
        }


        /*
         * Blue Star only.
         */

        if (
          cartType.type ===
          'blue_star_only'
        ) {

          if (
            cartType.isBulk
          ) {
            showState(
              container,
              '[data-bpg-state-direct-freight]'
            );
          }

          return;
        }


        /*
         * Third-party only.
         */

        if (
          cartType.type ===
          'third_party_only'
        ) {

          if (
            cartType.isBulk
          ) {
            showState(
              container,
              '[data-bpg-state-direct-freight]'
            );
          }

          return;
        }


        /*
         * Mixed cart.
         */

        if (
          cartType.isMixed &&
          cartType.isBulk
        ) {
          showState(
            container,
            '[data-bpg-state-direct-freight]'
          );
        }
      }
    );

    updateCheckoutState(
      cartType
    );
  }


  /* =========================================================
   CHECKOUT STATE
========================================================= */

function updateCheckoutState(
  cartType
) {
  const deliveryData =
    getDeliveryData();

  const isEligibleRetailBulkCart =
    cartType.isBulk &&
    cartType.isRetailOnly &&
    deliveryData &&
    deliveryData.eligible === true;


  const wrappers =
    document.querySelectorAll(
      '[data-bpg-checkout-control], [data-bpg-eligible-checkout]'
    );


  wrappers.forEach(
    function (wrapper) {

      /*
       * Show checkout for:
       *
       * 1. All normal carts below 25kg
       * 2. Retail Shop-only bulk carts
       *    that are eligible for Local Delivery
       *
       * Hide checkout for:
       *
       * - Blue Star bulk carts
       * - Third-party bulk carts
       * - Mixed bulk carts
       * - Retail bulk carts that are not eligible
       */

      wrapper.hidden =
        cartType.isBulk &&
        !isEligibleRetailBulkCart;
    }
  );

  


  /*
   * Keep bulk-order class only when
   * checkout should actually be blocked.
   */

  document.documentElement.classList.toggle(
    'bpg-bulk-order',
    cartType.isBulk &&
    !isEligibleRetailBulkCart
  );
}




  /* =========================================================
     LOCAL DELIVERY CHECK
  ========================================================= */

  async function checkDelivery(
    form
  ) {
    const modal =
      form.closest(
        '[data-bpg-local-delivery-modal]'
      );

    if (!modal) {
      return;
    }

    const addressInput =
      form.querySelector(
        '[name="address"]'
      );

    const suburbInput =
      form.querySelector(
        '[name="suburb"]'
      );

    const postcodeInput =
      form.querySelector(
        '[name="postcode"]'
      );

    const submitButton =
      form.querySelector(
        '[data-bpg-check-eligibility]'
      );


    const address =
      addressInput
        ? addressInput.value.trim()
        : '';

    const suburb =
      suburbInput
        ? suburbInput.value.trim()
        : '';

    const postcode =
      postcodeInput
        ? postcodeInput.value.trim()
        : '';


    hideModalMessage(modal);
    hideModalResult(modal);


    if (!address) {
      showModalMessage(
        modal,
        'Please enter your delivery address.'
      );

      return;
    }


    if (
      postcode &&
      !/^\d{4}$/.test(postcode)
    ) {
      showModalMessage(
        modal,
        'Please enter a valid 4-digit Australian postcode.'
      );

      return;
    }


    const originalButtonText =
      submitButton
        ? submitButton.textContent
        : 'Check Delivery Options';


    if (submitButton) {
      submitButton.disabled = true;

      submitButton.textContent =
        'Checking...';
    }


    try {
      const cart =
        await getCart();

      const cartType =
        getCartType(cart);


      /*
       * Safety check.
       * Local delivery form only applies
       * to Retail Shop-only carts.
       */

      if (
        !cartType.isRetailOnly
      ) {
        closeModal();

        updateCartDeliveryUI(
          cart
        );

        return;
      }


      const radius =
        parseFloat(
          modal.dataset.deliveryRadius ||
          '10'
        );


      const customerLocation =
        await geocodeCustomerAddress(
          address,
          suburb,
          postcode
        );

      if (!customerLocation) {
        throw new Error(
          'We could not find this address. Please check the address and try again.'
        );
      }


      const storeLocation =
        await geocodeStore(
          modal
        );

      if (!storeLocation) {
        throw new Error(
          'Store location could not be found.'
        );
      }


      const customerLat =
        parseFloat(
          customerLocation.lat
        );

      const customerLng =
        parseFloat(
          customerLocation.lon
        );

      const storeLat =
        parseFloat(
          storeLocation.lat
        );

      const storeLng =
        parseFloat(
          storeLocation.lon
        );


      if (
        [
          customerLat,
          customerLng,
          storeLat,
          storeLng
        ].some(Number.isNaN)
      ) {
        throw new Error(
          'Unable to calculate delivery distance.'
        );
      }


      const distance =
        getDistanceKm(
          storeLat,
          storeLng,
          customerLat,
          customerLng
        );

      const cartTotal =
        Number(
          cart.total_price || 0
        );

      const reason =
        getEligibilityReason(
          distance,
          radius,
          cartTotal
        );

      const eligible =
        reason === 'eligible';


      const fullAddress =
        [
          address,
          suburb,
          postcode,
          'Australia'
        ]
          .filter(Boolean)
          .join(', ');


      const deliveryData = {
        eligible: eligible,

        reason: reason,

        address: address,

        suburb: suburb,

        postcode: postcode,

        fullAddress: fullAddress,

        distance:
          Number(
            distance.toFixed(2)
          ),

        radius: radius,

        cartTotal: cartTotal,

        cartItemCount:
          Number(
            cart.item_count || 0
          ),

        cartWeight:
          Number(
            cart.total_weight || 0
          ),

        checkedAt:
          new Date().toISOString()
      };


      saveDeliveryData(
        deliveryData
      );

      await saveEligibilityToCart(
        deliveryData
      );

      updateCartDeliveryUI(
        cart
      );


      /*
       * Eligible.
       */

      if (eligible) {
        showModalResult(
          modal,
          `
            <strong>
              Local Delivery Available
            </strong>

            <p>
              Great news! Your delivery address is within our local delivery area and your order meets the $150 minimum order requirement.
            </p>

            <button
              type="button"
              class="bpg-local-delivery-result__button"
              data-bpg-confirm-local-delivery
            >
              Continue
            </button>
          `,
          true
        );

        return;
      }


      /*
       * Heavy cart:
       * not eligible = freight quote.
       */

      if (
        cartType.isBulk
      ) {
        showModalResult(
          modal,
          `
            <strong>
              Freight Quote Required
            </strong>

            <p>
              ${getEligibilityMessage(reason)}
            </p>

            <button
              type="button"
              class="bpg-local-delivery-result__button"
              data-bpg-request-freight-quote
            >
              Request a Freight Quote
            </button>
          `,
          false
        );

        return;
      }


      /*
       * Below 25kg:
       * normal shipping remains available.
       */

      showModalResult(
        modal,
        `
          <strong>
            Local Delivery Not Available
          </strong>

          <p>
            ${getEligibilityMessage(reason)}
          </p>

          <p>
            You can continue to checkout and use the available shipping options.
          </p>

          <button
            type="button"
            class="bpg-local-delivery-result__button"
            data-bpg-confirm-local-delivery
          >
            Continue
          </button>
        `,
        false
      );

    } catch (error) {

      console.error(
        'BPG delivery check error:',
        error
      );

      showModalMessage(
        modal,
        error.message ||
        'Something went wrong while checking your address. Please try again.'
      );

    } finally {

      if (submitButton) {
        submitButton.disabled = false;

        submitButton.textContent =
          originalButtonText;
      }
    }
  }


  /* =========================================================
     CART STATE
  ========================================================= */

  async function refreshCartState() {
    try {
      const cart =
        await getCart();

      const cartType =
        getCartType(cart);

      const deliveryData =
        getDeliveryData();


      /*
       * Clear delivery state if cart is empty.
       */

      if (
        cartType.isEmpty
      ) {
        clearDeliveryData();

        updateCartDeliveryUI(
          cart
        );

        return;
      }


      /*
       * Delivery data only belongs
       * to Retail Shop-only carts.
       */

      if (
        !cartType.isRetailOnly
      ) {
        if (deliveryData) {
          clearDeliveryData();

          await saveEligibilityToCart(
            null
          );
        }

        updateCartDeliveryUI(
          cart
        );

        return;
      }


      /*
       * Revalidate saved eligibility
       * when cart total changes.
       */

      if (
        deliveryData
      ) {
        const radius =
          Number(
            deliveryData.radius || 10
          );

        const reason =
          getEligibilityReason(
            Number(
              deliveryData.distance || 0
            ),
            radius,
            Number(
              cart.total_price || 0
            )
          );

        const updatedData = {
          ...deliveryData,

          eligible:
            reason === 'eligible',

          reason: reason,

          cartTotal:
            Number(
              cart.total_price || 0
            ),

          cartItemCount:
            Number(
              cart.item_count || 0
            ),

          cartWeight:
            Number(
              cart.total_weight || 0
            ),

          updatedAt:
            new Date().toISOString()
        };


        if (
          updatedData.eligible !==
          deliveryData.eligible
        ) {
          await saveEligibilityToCart(
            updatedData
          );
        }

        saveDeliveryData(
          updatedData
        );
      }


      updateCartDeliveryUI(
        cart
      );

    } catch (error) {
      console.warn(
        'BPG cart refresh failed:',
        error
      );
    }
  }


  function scheduleCartRefresh(
    delay
  ) {
    clearTimeout(
      refreshTimer
    );

    refreshTimer =
      setTimeout(
        refreshCartState,
        typeof delay === 'number'
          ? delay
          : 250
      );
  }


  /* =========================================================
     EVENT HANDLING
  ========================================================= */

  document.addEventListener(
    'submit',
    function (event) {

      const form =
        event.target.closest(
          '[data-bpg-local-delivery-form]'
        );

      if (!form) {
        return;
      }

      event.preventDefault();

      checkDelivery(
        form
      );
    },
    true
  );


  document.addEventListener(
    'click',
    function (event) {

      const target =
        event.target;


      /*
       * Open delivery modal.
       */

      if (
        target.closest(
          '[data-bpg-open-delivery-options]'
        )
      ) {
        event.preventDefault();

        openModal();

        return;
      }


      /*
       * Close modal.
       */

      if (
        target.closest(
          '[data-bpg-close-local-delivery]'
        )
      ) {
        event.preventDefault();

        closeModal();

        return;
      }

    /*
    * Pickup from Retail Shop
    */
    if (
      target.closest(
        '[data-bpg-local-pickup][data-bpg-pickup-checkout]'
      )
    ) {
      event.preventDefault();

      const pickupButton = target.closest(
        '[data-bpg-local-pickup][data-bpg-pickup-checkout]'
      );

      if (pickupButton) {
        pickupButton.disabled = true;
        pickupButton.textContent = 'Preparing Pickup...';
      }

      preparePickupCheckout();

      return;
    }


      /*
       * Continue.
       */

      if (
        target.closest(
          '[data-bpg-confirm-local-delivery]'
        )
      ) {
        event.preventDefault();

        closeModal();

        scheduleCartRefresh(
          0
        );

        return;
      }


      /*
       * Change address.
       */

      if (
        target.closest(
          '[data-bpg-change-delivery-address]'
        )
      ) {
        event.preventDefault();

        openModal();

        return;
      }


      /*
       * Freight quote.
       */

      if (
        target.closest(
          '[data-bpg-request-freight-quote]'
        )
      ) {
        event.preventDefault();

        closeModal();

        const trigger =
          document.querySelector(
            '[data-freight-quote-trigger]'
          );

        if (trigger) {
          trigger.click();
        }

        return;
      }
    }
  );


  document.addEventListener(
    'keydown',
    function (event) {
      if (
        event.key === 'Escape'
      ) {
        closeModal();
      }
    }
  );


  /*
   * Enterprise theme cart events.
   */

  [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'shopify:section:load'
  ].forEach(
    function (eventName) {
      document.addEventListener(
        eventName,
        function () {
          scheduleCartRefresh(
            300
          );
        }
      );
    }
  );


  /*
   * Lightweight fetch detection.
   *
   * No full-page MutationObserver.
   */

  const originalFetch =
    window.fetch;

  window.fetch =
    async function () {

      const response =
        await originalFetch.apply(
          this,
          arguments
        );

      try {

        const request =
          arguments[0];

        const url =
          typeof request === 'string'
            ? request
            : (
                request &&
                request.url
                  ? request.url
                  : ''
              );

        if (
          url &&
          (
            url.includes('/cart/add') ||
            url.includes('/cart/change') ||
            url.includes('/cart/update') ||
            url.includes('/cart/clear')
          )
        ) {
          scheduleCartRefresh(
            400
          );
        }

      } catch (error) {
        /* Ignore detection errors */
      }

      return response;
    };


  /* =========================================================
     INIT
  ========================================================= */

  function init() {
    refreshCartState();
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }

/* =========================================================
   PICKUP CHECKOUT PREFERENCE
========================================================= */

async function preparePickupCheckout() {
  await updateCartAttributes({
    'BPG Preferred Fulfillment': 'Pickup'
  });

  window.location.href = '/checkout';
}


  /* =========================================================
     PUBLIC HELPERS
  ========================================================= */

  window.bpgRefreshDeliveryFlow =
    scheduleCartRefresh;

})();